/**
 * Reports API Hooks
 * React hooks for reports endpoints
 * Based on reports-controller from admin-docs.json
 */

import { useFetch, buildUrl, useMutation } from './useApi';
import { ADMIN_API_BASE_URL, RIDER_API_BASE_URL } from '../config';
import { getAuthToken } from './useAdminRefactored';
import type {
  DateRangeParams,
  RideHistoryResponse,
  RideAnalysisResponse,
  RevenueRideTrendResponse,
  RevenueBreakdownResponse,
  DriverPerformanceTiersResponse,
  CompletedRidesHourlyResponse,
} from './reportsTypes';
import type { Pageable } from './types';

const API_BASE_URL = RIDER_API_BASE_URL;
const API_BASE_URL_ADMIN = ADMIN_API_BASE_URL;
/**
 * Get authentication headers
 */
function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  // return token ? { Authorization: `Bearer ${token}` } : {};
  return {};
}

// ============================================================================
// Ride History Endpoints
// ============================================================================

/**
 * Hook to fetch ride history with pagination
 * GET /reports/ride-history
 * Matches API: from, to (ISO datetime), page (1-based), size, sort (JSON array string e.g. ["string"])
 */
export function useRideHistory(
  params: DateRangeParams & { pageable: Pageable },
  options: {
    enabled?: boolean;
    onSuccess?: (data: RideHistoryResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // API expects 0-based page index (Pageable.page minimum: 0 per admin.json)
  const page = params.pageable?.page ?? 0;
  const queryParams: Record<string, any> = {
    page,
    size: params.pageable?.size ?? 10,
  };

  if (params.from) {
    queryParams.from = params.from;
  }
  if (params.to) {
    queryParams.to = params.to;
  }
  if (params.status) {
    queryParams.status = params.status;
  }
  if (params.search) {
    queryParams.search = params.search;
  }
  // Backend expects sort as JSON array string: sort=["string"] -> sort=%5B%22string%22%5D
  if (params.pageable?.sort && params.pageable.sort.length > 0) {
    queryParams.sort = JSON.stringify(params.pageable.sort);
  }

  const url = buildUrl(ADMIN_API_BASE_URL, '/reports/ride-history', queryParams);

  return useFetch<RideHistoryResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError ? options.onError : (error) => {
      // Silently ignore 404 and 500 errors - endpoint may not be implemented or may have issues
      if (!error.message.includes('404') && !error.message.includes('500')) {
        console.error('Failed to load ride history:', error);
      }
    },
  });
}

/**
 * Hook to fetch ride history for a specific rider
 * GET /ride/ride-history-rider/{riderId}
 */
export function useRiderRideHistory(
  riderId: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: RideHistoryResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL, `/ride/ride-history-rider/${riderId}`);

  return useFetch<RideHistoryResponse>(url, {
    enabled: options.enabled !== false && !!riderId,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch ride history for a specific driver
 * GET /ride/ride-history-driver/{driverId}
 */
export function useDriverRideHistory(
  driverId: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: RideHistoryResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL, `/ride/ride-history-driver/${driverId}`);

  return useFetch<RideHistoryResponse>(url, {
    enabled: options.enabled !== false && !!driverId,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch fare breakdown by bookingId
 * GET /ride/fare/breakdown/{bookingId}
 */
export function useFareBreakdown(
  bookingId: number,
  options: {
    enabled?: boolean;
    onSuccess?: (data: any) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(
    API_BASE_URL,
    `/ride/fare/breakdown/${bookingId}`
  );

  return useFetch<any>(url, {
    enabled: options.enabled !== false && !!bookingId,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to cancel/end an ongoing ride
 * GET /ride/cancel/{bookingId}?cancelledBy={cancelledBy}&remarks={remarks}
 */
export function useCancelRide(
  options: {
    onSuccess?: (data: any) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<any, { bookingId: number; cancelledBy: string; remarks: string }>(
    async (variables) => {
      const url = buildUrl(API_BASE_URL, `/ride/cancel/${variables.bookingId}`, {
        cancelledBy: variables.cancelledBy,
        remarks: variables.remarks,
      });

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const text = await response.text();
      return text ? JSON.parse(text) : {};
    },
    {
      onSuccess: (data, variables) => options.onSuccess?.(data),
      onError: (error, variables) => options.onError?.(error),
    }
  );
}

/**
 * Hook to export ride history as PDF
 * POST /reports/ride-history-export-pdf
 */
export function useExportRideHistoryPdf(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<Blob, DateRangeParams>(
    async (params) => {
      const queryParams: Record<string, any> = {};
      if (params.from) {
        queryParams.from = params.from;
      }
      if (params.to) {
        queryParams.to = params.to;
      }
      if (params.search) {
        queryParams.search = params.search;
      }
      if (params.status) {
        queryParams.status = params.status;
      }

      const url = buildUrl(ADMIN_API_BASE_URL, '/reports/ride-history-export-pdf', queryParams);

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.blob();
    },
    {
      onSuccess: (blob, params) => {
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = safeExportFilename(params.from, params.to, 'pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: (error, params) => {
        options.onError?.(error);
      },
    }
  );
}

/** MIME types */
const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const CSV_MIME = 'text/csv';

/** Check if a blob is a genuine XLSX file (ZIP/OpenXML starts with PK magic bytes) */
async function isGenuineXlsx(blob: Blob): Promise<boolean> {
  if (blob.size < 4) return false;
  const header = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
  // XLSX (ZIP) files start with PK\x03\x04
  return header[0] === 0x50 && header[1] === 0x4B && header[2] === 0x03 && header[3] === 0x04;
}

/** Sanitize filename: remove colons and other chars invalid on Windows */
function safeExportFilename(from?: string, to?: string, ext: string = 'xlsx'): string {
  const sanitize = (s: string) => s.replace(/[:/\\?*"<>|]/g, '-').replace(/\s+/g, '-');
  const fromPart = from ? sanitize(from) : 'all';
  const toPart = to ? sanitize(to) : 'all';
  return `ride-history-${fromPart}-${toPart}.${ext}`;
}

/**
 * Hook to export ride history as Excel
 * POST /reports/ride-history-export-excel
 * Handles both raw binary and JSON-wrapped (e.g. base64) responses so the file opens in Excel.
 */
export function useExportRideHistoryExcel(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<{ blob: Blob; ext: string }, DateRangeParams>(
    async (params) => {
      const queryParams: Record<string, any> = {};
      if (params.from) {
        queryParams.from = params.from;
      }
      if (params.to) {
        queryParams.to = params.to;
      }
      if (params.search) {
        queryParams.search = params.search;
      }
      if (params.status) {
        queryParams.status = params.status;
      }

      const url = buildUrl(ADMIN_API_BASE_URL, '/reports/ride-history-export-excel', queryParams);

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = (response.headers.get('Content-Type') || '').toLowerCase();

      let blob: Blob;

      // Backend may return JSON with base64 data (e.g. ApiResponse wrapper)
      if (contentType.includes('application/json')) {
        const json = await response.json();
        const base64 =
          typeof json === 'string'
            ? json
            : (json.data ?? json.content ?? json.file ?? json);
        const binaryString = typeof base64 === 'string' ? atob(base64) : '';
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: EXCEL_MIME });
      } else {
        blob = await response.blob();
      }

      // Detect actual file format by checking magic bytes
      const isXlsx = await isGenuineXlsx(blob);

      if (isXlsx) {
        // Genuine XLSX — ensure correct MIME type
        if (blob.type !== EXCEL_MIME) {
          const buffer = await blob.arrayBuffer();
          blob = new Blob([buffer], { type: EXCEL_MIME });
        }
        return { blob, ext: 'xlsx' };
      } else {
        // Not a real XLSX — likely CSV/text data. Save as CSV so MS Excel can open it.
        if (!blob.type || !blob.type.includes('csv')) {
          const buffer = await blob.arrayBuffer();
          blob = new Blob([buffer], { type: CSV_MIME });
        }
        return { blob, ext: 'csv' };
      }
    },
    {
      onSuccess: (result, params) => {
        const url = window.URL.createObjectURL(result.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = safeExportFilename(params.from, params.to, result.ext);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: (error, params) => {
        options.onError?.(error);
      },
    }
  );
}

// ============================================================================
// Financial Reports Export Endpoints
// ============================================================================

/**
 * Hook to export financial reports as PDF
 * POST /reports/financial-reports-export-pdf
 */
export function useExportFinancialReportsPdf(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<Blob, DateRangeParams>(
    async (params) => {
      const queryParams: Record<string, any> = {};
      if (params.from) queryParams.from = params.from;
      if (params.to) queryParams.to = params.to;
      if (params.search) queryParams.search = params.search;
      if (params.status) queryParams.status = params.status;

      const url = buildUrl(ADMIN_API_BASE_URL, '/reports/financial-reports-export-pdf', queryParams);

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return response.blob();
    },
    {
      onSuccess: (blob, params) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `financial-reports-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: options.onError,
    }
  );
}

/**
 * Hook to export financial reports as Excel
 * POST /reports/financial-reports-export-excel
 */
export function useExportFinancialReportsExcel(
  options: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
  } = {}
) {
  return useMutation<Blob, DateRangeParams>(
    async (params) => {
      const queryParams: Record<string, any> = {};
      if (params.from) queryParams.from = params.from;
      if (params.to) queryParams.to = params.to;
      if (params.search) queryParams.search = params.search;
      if (params.status) queryParams.status = params.status;

      const url = buildUrl(ADMIN_API_BASE_URL, '/reports/financial-reports-export-excel', queryParams);

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = (response.headers.get('Content-Type') || '').toLowerCase();

      // Backend may return JSON with base64 data
      if (contentType.includes('application/json')) {
        const json = await response.json();
        const base64 =
          typeof json === 'string'
            ? json
            : (json.data ?? json.content ?? json.file ?? json);
        const binaryString = typeof base64 === 'string' ? atob(base64) : '';
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return new Blob([bytes], { type: EXCEL_MIME });
      }

      let blob = await response.blob();
      if (!blob.type || blob.type === 'application/json' || blob.type === 'application/octet-stream') {
        const buffer = await blob.arrayBuffer();
        blob = new Blob([buffer], { type: EXCEL_MIME });
      }
      return blob;
    },
    {
      onSuccess: (blob, params) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `financial-reports-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        options.onSuccess?.();
      },
      onError: options.onError,
    }
  );
}

// ============================================================================
// Ride Analysis Endpoints
// ============================================================================

/**
 * Hook to fetch total ride analysis
 * GET /reports/ride-analysis
 */
export function useRideAnalysis(
  params: DateRangeParams = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: RideAnalysisResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryParams: Record<string, any> = {};
  if (params.from) {
    queryParams.from = params.from;
  }
  if (params.to) {
    queryParams.to = params.to;
  }

  const url = buildUrl(ADMIN_API_BASE_URL, '/reports/ride-analysis', queryParams);
  
  return useFetch<RideAnalysisResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

// ============================================================================
// Revenue Endpoints
// ============================================================================

// Financial Reports Response
export interface FinancialReportItem {
  id?: number;
  date?: string;
  description?: string;
  amount?: number;
  type?: string;
  status?: string;
  [key: string]: any;
}

export type FinancialReportsResponse = ApiResponse<{
  content: FinancialReportItem[];
  page: {
    size: number;
    number: number;
    totalElements: number;
    totalPages: number;
  };
}>;

/**
 * Hook to fetch financial reports with pagination
 * GET /reports/financial-reports
 */
export function useFinancialReports(
  params: DateRangeParams & { pageable?: Pageable } = {},
  options: {
    enabled?: boolean;
    onSuccess?: (data: FinancialReportsResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryParams: Record<string, any> = {};
  if (params.from) queryParams.from = params.from;
  if (params.to) queryParams.to = params.to;
  if (params.search) queryParams.search = params.search;
  if (params.status) queryParams.status = params.status;
  if (params.pageable) {
    queryParams.page = params.pageable.page ?? 0;
    queryParams.size = params.pageable.size ?? 10;
    if (params.pageable.sort) {
      queryParams.sort = JSON.stringify(params.pageable.sort);
    }
  }

  const url = buildUrl(ADMIN_API_BASE_URL, '/reports/financial-reports', queryParams);

  return useFetch<FinancialReportsResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch revenue and ride trend for last 6 months
 * GET /reports/revenue-ride-trend-last-six-months
 */
export function useRevenueRideTrend(
  options: {
    enabled?: boolean;
    onSuccess?: (data: RevenueRideTrendResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(ADMIN_API_BASE_URL, '/reports/revenue-ride-trend-last-six-months');
  
  return useFetch<RevenueRideTrendResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch revenue breakdown for last 6 months
 * GET /reports/revenue-last-six-months
 */
export function useRevenueBreakdown(
  options: {
    enabled?: boolean;
    onSuccess?: (data: RevenueBreakdownResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(ADMIN_API_BASE_URL, '/reports/revenue-last-six-months');
  
  return useFetch<RevenueBreakdownResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

// ============================================================================
// Driver Performance Endpoints
// ============================================================================

/**
 * Hook to fetch driver performance tiers
 * GET /reports/driver-performance-tiers
 */
export function useDriverPerformanceTiers(
  options: {
    enabled?: boolean;
    onSuccess?: (data: DriverPerformanceTiersResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(ADMIN_API_BASE_URL, '/reports/driver-performance-tiers');
  
  return useFetch<DriverPerformanceTiersResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

// ============================================================================
// Completed Rides Hourly Endpoints
// ============================================================================

/**
 * Hook to fetch completed rides every 4 hours
 * GET /reports/completed-rides-hourly-4-hours
 */
export function useCompletedRidesHourly(
  date?: string, // ISO date format (YYYY-MM-DD)
  options: {
    enabled?: boolean;
    onSuccess?: (data: CompletedRidesHourlyResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const queryParams: Record<string, any> = {};
  if (date) {
    queryParams.date = date;
  }

  const url = buildUrl(ADMIN_API_BASE_URL, '/reports/completed-rides-hourly-4-hours', queryParams);
  
  return useFetch<CompletedRidesHourlyResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}
